import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AdminModuleContent, AdminNavigation } from '@/components/AdminPortal';
import { PageContainer } from '@/components/Shared/PageContainer';
import { appConfig } from '@/config/appConfig';
import { routes } from '@/config/routes';
import { layout } from '@/constants/layout';
import {
  portalEntityId,
  selectedProductQueryParam,
  selectedProductStorageKey,
} from '@/constants/productSelection';
import { useAdminNavigation } from '@/hooks/useAdminNavigation';
import { useProducts } from '@/hooks/useProducts';
import { PortalLayout } from '@/layouts/PortalLayout';
import { useAppShell } from '@/shell';
import type { EntityOption } from '@/types/entity';

export function AdminPage() {
  const { t } = useTranslation('entity-portal');
  const { setCurrentProductId, setBreadcrumbs } = useAppShell();
  const navigate = useNavigate();
  const { selectedModule, selectedModuleId, selectModule } =
    useAdminNavigation();
  const { data: products = [] } = useProducts();

  const headerEntities = useMemo<EntityOption[]>(() => {
    const productEntities = products.map((product) => ({
      id: product.id,
      label: t(product.name, { defaultValue: product.name }),
    }));

    return [
      {
        id: portalEntityId,
        label: appConfig.name,
      },
      ...productEntities,
    ];
  }, [products, t]);

  const moduleTitle = t(selectedModule.nameKey);

  useEffect(() => {
    setCurrentProductId(null);
    setBreadcrumbs([
      { label: t('admin.adminDashboardBreadcrumbPortal') },
      { label: moduleTitle },
    ]);
  }, [moduleTitle, setBreadcrumbs, setCurrentProductId, t]);

  const handleEntityChange = useCallback(
    (entity: EntityOption) => {
      if (entity.id === portalEntityId) {
        navigate(routes.home);
        return;
      }

      window.localStorage.setItem(selectedProductStorageKey, entity.id);
      navigate({
        pathname: routes.home,
        search: `?${selectedProductQueryParam}=${encodeURIComponent(entity.id)}`,
      });
    },
    [navigate],
  );

  const handleLogoClick = useCallback(() => {
    navigate(routes.home);
  }, [navigate]);

  const sidebar = (
    <AdminNavigation
      selectedModuleId={selectedModuleId}
      onSelect={selectModule}
    />
  );

  return (
    <PortalLayout
      sidebar={sidebar}
      showSidebar
      sidebarWidth={layout.adminSidebarWidth}
      entities={headerEntities}
      selectedEntityId={portalEntityId}
      onEntityChange={handleEntityChange}
      onLogoClick={handleLogoClick}
      entitySelectorDisabled={products.length === 0}
    >
      <PageContainer>
        <AdminModuleContent
          module={selectedModule}
          moduleId={selectedModuleId}
          title={moduleTitle}
        />
      </PageContainer>
    </PortalLayout>
  );
}
